import { useEffect, useRef } from 'react';
import CryptoJS from 'crypto-js';
import { supabase } from '../supabase';
import { retryQuery } from '../utils/retryQuery';
import { DEFAULT_CHANNELS } from '../constants/defaults';
import { preloadImage, preloadImages } from '../services/imageCache';
import {
  FIXTURES_ENABLED,
  fixtureParticipantsForChannel,
  fixturePeerVoiceStates,
} from '../fixtures/callFixtures';
import { Message, Channel } from '../types/chat';

/**
 * Attempt to decrypt `ciphertext` with the local vault key. Returns
 * `{ ok: true, plaintext }` when the cipher roundtrips to valid UTF-8, and
 * `{ ok: false, looksEncrypted }` when the input was non-empty, looked
 * base64-ish (long, only [A-Za-z0-9+/=]), but did not decrypt — which is the
 * "silently leaks ciphertext" failure mode the audit flagged. Returns
 * `{ ok: true, plaintext: ciphertext }` when the input is empty/plain so
 * we don't false-positive on newly-typed messages.
 */
// CryptoJS AES ciphertext always begins with the magic prefix
// `U2FsdGVkX1` (base64 of "Salted__"). Match on that — much more
// accurate than a broad base64 regex, which would mis-flag any long
// alphanumeric token / base64 URL as "looks encrypted".
const looksLikeCiphertext = (s: string): boolean => /^U2FsdGVkX1/.test(s);

function tryDecrypt(ciphertext: string | null | undefined, key: string): { ok: boolean; plaintext: string; looksEncrypted: boolean } {
  if (!ciphertext) return { ok: true, plaintext: '', looksEncrypted: false };
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, key);
    const plaintext = bytes.toString(CryptoJS.enc.Utf8);
    if (plaintext && plaintext.length > 0) {
      return { ok: true, plaintext, looksEncrypted: false };
    }
    // Empty plaintext means the decrypt roundtrip produced no UTF-8 —
    // either the data was not encrypted, or the key was wrong.
    return { ok: false, plaintext: ciphertext, looksEncrypted: looksLikeCiphertext(ciphertext) };
  } catch {
    return { ok: false, plaintext: ciphertext, looksEncrypted: looksLikeCiphertext(ciphertext) };
  }
}

/**
 * Fields the vault sidebar consumes from each vault row. Realtime
 * postgres_changes payloads carry every column; we deliberately keep only
 * what the UI reads (and never password_hash — column-revoked server-side
 * anyway). Used both for the initial fetch mapping and for live patches so
 * locally-applied rows stay shape-identical to fetched rows.
 */
function pickVaultRow(row: any): any {
  return {
    id: row.id,
    name: row.name,
    type: row.type,
    created_by: row.created_by,
    members: row.members,
    last_message_at: row.last_message_at,
    customization: row.customization ?? {},
    icon_url: row.icon_url,
    icon_hue: row.icon_hue,
  };
}

/**
 * Peer voice-state shape — a snapshot of every other call participant
 * that the local UI displays (mutes, cameras, screenshare) plus the new
 * 🎮 game-activity fields. Kept here as the single source of truth so the
 * presence payload, the React-visible state, and the local newPeerStates
 * map inside the presence-sync handler cannot drift from one another.
 */
export type PeerVoiceState = {
  isMuted: boolean;
  isCameraOn: boolean;
  isScreenSharing: boolean;
  currentGame?: string | null;
  currentGameSessionId?: string | null;
};

export interface UseRealtimeSyncParams {
  isAuthenticated: boolean;
  currentUser: any;
  userProfile: any;
  activeVault: any;
  setActiveVault: (vault: any) => void;
  vaults: any[];
  // Accepts functional updaters too — the realtime patcher reorders/replaces
  // rows from payloads without a refetch. App.tsx passes the raw useState
  // setter (which supports updaters); the vault store's setVaults does too.
  setVaults: (vaults: any[] | ((prev: any[]) => any[])) => void;
  setDmRooms: React.Dispatch<React.SetStateAction<any[]>>;
  setVaultsLoading: (loading: boolean) => void;
  activeChannelId: string | null;
  setChannels: (c: any[]) => void;
  categories: any[];
  setCategories: (c: any[]) => void;
  isCallActive: boolean;
  isCallActiveRef: React.RefObject<boolean>;
  callChannelId: string | null;
  isMuted: boolean;
  isVideoOn: boolean;
  isScreenSharing: boolean;
  isWatchTogetherHost: boolean;
  /** Current game the local user is playing, if any. When set, this drives
   * the 🎮 game-activity pill that other voice-channel participants see.
   * Optional so existing App.tsx call sites keep typechecking unchanged. */
  currentGame?: string | null;
  /** Session id for the current game activity, used for Join-Game UX.
   * Optional so existing App.tsx call sites keep typechecking unchanged. */
  currentGameSessionId?: string | null;
  setVoiceParticipants: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
  voiceParticipants: Record<string, any[]>;
  setPeerVoiceStates: React.Dispatch<React.SetStateAction<Record<string, PeerVoiceState>>>;
  webRTCManagerRef: React.RefObject<any>;
  activeContactId: string | null;
  dmRooms: any[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setMessagesLoading: (loading: boolean) => void;
  setTypingUsers: (typing: Record<string, boolean>) => void;
  addNotification: (notification: any) => void;
  playSound: (sound: string) => void;
}

export function useRealtimeSync({
  isAuthenticated,
  currentUser,
  userProfile,
  activeVault,
  setActiveVault,
  vaults,
  setVaults,
  setDmRooms,
  setVaultsLoading,
  activeChannelId,
  setChannels,
  categories,
  setCategories,
  isCallActive,
  isCallActiveRef,
  callChannelId,
  isMuted,
  isVideoOn,
  isScreenSharing,
  isWatchTogetherHost,
  currentGame = null,
  currentGameSessionId = null,
  setVoiceParticipants,
  voiceParticipants,
  setPeerVoiceStates,
  webRTCManagerRef,
  activeContactId,
  dmRooms,
  setMessages,
  setMessagesLoading,
  setTypingUsers,
  addNotification,
  playSound,
}: UseRealtimeSyncParams) {
  const voicePresenceChannelRef = useRef<any>(null);
  const lastTrackedChannelRef = useRef<string | null>(null);
  const callChannelIdRef = useRef<string | null>(callChannelId);

  useEffect(() => {
    callChannelIdRef.current = callChannelId;
  }, [callChannelId]);


  // 1. Vaults changes subscription
  // Uses an AbortController per effect run so that when the user signs out
  // (or out-and-back-in as a different user — the auth-swap race flagged in
  // Section 9.7), the in-flight fetch for the previous user is aborted
  // before its setState lands for the new user.
  useEffect(() => {
    if (!isAuthenticated) return;

    const abortCtrl = new AbortController();
    const { signal } = abortCtrl;
    // Stable user id for the lifetime of this effect — used by both
    // fetchVaults and the realtime patcher below for membership checks.
    const uid = currentUser?.id;

    const fetchVaults = async () => {
      if (signal.aborted || !uid) return;

      const { data: allVaults, error } = await retryQuery(() =>
        supabase
          .from('vaults')
          .select('id, name, type, created_by, members, last_message_at, customization, icon_url, icon_hue')
          // Push the "which vaults are mine" filter down to Postgres (cs =
          // array-contains) instead of pulling every RLS-visible vault —
          // including public vaults we don't belong to — and discarding
          // them client-side. The members GIN index (20260921020000) makes
          // this a narrow lookup, and the payload shrinks to the ~few rows
          // this user actually sees in the sidebar.
          .or(`created_by.eq.${uid},members.cs.{${uid}}`)
          .order('created_at', { ascending: false })
      );

      if (signal.aborted) return;
      if (error) {
        console.error("Error fetching vaults:", error);
        setVaultsLoading(false);
        return;
      }

      const pRooms = (allVaults || []).filter(v => {
        if (v.type === 'dm') return false;
        if (v.created_by === uid) return true;
        // Membership is the `vaults.members` uuid[] — the same source the
        // dRooms filter below uses. This previously read a `vault_members`
        // junction table that does not exist in this schema (see the
        // documented gap in 20260719000000_p1_11_rls_audit.sql), so the
        // lookup always came back empty and this branch was dead. Result:
        // the sidebar only ever listed servers you CREATED, and a server
        // you joined never appeared — "says I joined but I don't see it".
        if (v.members?.includes(uid)) return true;
        return false;
      });

      const dRooms = (allVaults || []).filter(v => v.type === 'dm' && v.members?.includes(uid)).sort((a, b) =>
        new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime()
      );

      setVaults(pRooms);
      setDmRooms(dRooms);
      setVaultsLoading(false);

      // Batch preload all server banners so switching vaults is instant.
      const bannerUrls = pRooms
        .map((v: any) => v?.customization?.bannerImage)
        .filter((url: string | undefined): url is string => !!url);
      if (bannerUrls.length > 0) preloadImages(bannerUrls);

    };

    fetchVaults();

    // The `vaults` postgres_changes subscription is unfiltered (no server-side
    // filter is possible here — a user's vaults are spread across rows they
    // don't own), so EVERY vault change by ANY user on the whole app wakes up
    // EVERY connected client. Worse, `vaults.last_message_at` is bumped on
    // EVERY message sent anywhere (App.tsx sendMessage), so the old
    // refetch-on-every-event handler effectively re-ran the expensive vaults
    // query on every message burst. pg_stat_statements measured that query
    // averaging 2.4s (peaking at 7.7s) on the current compute tier; stacked
    // bursts held connections open and starved the pool until requests hit
    // the statement timeout (the 500s on /rest/v1/vaults).
    //
    // The realtime payload carries the changed ROW (RLS-filtered to what this
    // client may see), so we patch the local vault / DM lists straight from the
    // payload — zero queries for the overwhelming majority of events (message
    // bumps, name/icon/customization edits, membership adds/removes). The full
    // refetch is reduced to a rare fallback: ambiguous payloads, channel
    // (re)subscribes, and a 5-minute drift-sync safety net.
    let refetchTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefetch = () => {
      clearTimeout(refetchTimer);
      refetchTimer = setTimeout(() => {
        if (!signal.aborted) fetchVaults();
      }, 800);
    };

    const belongsToUser = (row: any) =>
      !!row && (row.created_by === uid || (Array.isArray(row.members) && row.members.includes(uid)));

    const dropRowLocally = (id: string | undefined) => {
      if (!id) return;
      setVaults(prev => (prev.some(v => v.id === id) ? prev.filter(v => v.id !== id) : prev));
      setDmRooms(prev => (prev.some(r => r.id === id) ? prev.filter(r => r.id !== id) : prev));
    };

    const handleVaultChange = (payload: any) => {
      if (signal.aborted || !uid) return;
      const { eventType, new: row, old: oldRow } = payload ?? {};
      const id = row?.id ?? oldRow?.id;

      if (eventType === 'DELETE') {
        dropRowLocally(id);
        return;
      }

      // No usable row (e.g. RLS hid the change from us) — can't patch, so fall
      // back to the debounced full refetch.
      if (!row || !id) {
        scheduleRefetch();
        return;
      }

      if (!belongsToUser(row)) {
        // The row changed but is not ours anymore (we left / were removed /
        // it stopped being a DM). Drop it locally if we still had it.
        dropRowLocally(id);
        return;
      }

      const clean = pickVaultRow(row);
      // Keep the banner cache warm for vaults whose banner just changed.
      const banner = clean.customization?.bannerImage;
      if (banner) preloadImage(banner);

      if (clean.type === 'dm') {
        setDmRooms(prev => {
          const idx = prev.findIndex(r => r.id === clean.id);
          if (idx === -1) return [clean, ...prev];
          const existing = prev[idx];
          const next = [...prev];
          next[idx] = { ...existing, ...clean };
          // DMs bubble to the top on new activity, matching the initial fetch.
          // Skip the re-sort when activity time didn't change (e.g. a name or
          // icon edit) to avoid needless re-renders.
          if (existing.last_message_at === clean.last_message_at) return next;
          return next.sort(
            (a, b) => new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime()
          );
        });
      } else {
        setVaults(prev => {
          const idx = prev.findIndex(v => v.id === clean.id);
          if (idx === -1) return [clean, ...prev];
          const next = [...prev];
          next[idx] = { ...next[idx], ...clean };
          return next;
        });
      }
    };

    let isFirstSubscribe = true;
    const channel = supabase
      .channel('vaults_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vaults' }, (payload) => {
        handleVaultChange(payload);
      })
      .subscribe((status) => {
        // After any (re)subscribe, resync in case events were missed while the
        // channel was down. The first SUBSCRIBED lands right after the initial
        // fetchVaults() above, so skip it to avoid a redundant double fetch.
        if (status === 'SUBSCRIBED') {
          if (isFirstSubscribe) {
            isFirstSubscribe = false;
          } else {
            scheduleRefetch();
          }
        }
      });

    // Periodic drift-sync safety net: local patching covers every event we
    // receive, but a silently dropped event (reconnect gap, channel hiccup) is
    // caught by an occasional cheap refetch instead of never.
    const driftTimer = setInterval(() => {
      if (!signal.aborted) scheduleRefetch();
    }, 5 * 60 * 1000);

    return () => {
      clearTimeout(refetchTimer);
      clearInterval(driftTimer);
      abortCtrl.abort();
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated]);

  // 2. Channels changes subscription
  useEffect(() => {
    if (!isAuthenticated || !activeVault) { setChannels(DEFAULT_CHANNELS); return; }

    const fetchChannelsAndCategories = async () => {
      const [chRes, catRes] = await Promise.all([
        supabase.from('channels').select('id, name, type, customization').eq('vault_id', activeVault.id),
        supabase.from('categories').select('id, name, position').eq('vault_id', activeVault.id).order('position', { ascending: true })
      ]);

      if (chRes.data && chRes.data.length > 0) {
        const mapped = chRes.data.map(ch => ({ ...ch, topic: ch.customization?.topic || '' }));
        const sorted = mapped.sort((a, b) => {
          if (a.type !== b.type) return a.type === 'text' ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
        setChannels(sorted);
      } else {
        setChannels(DEFAULT_CHANNELS);
      }

      if (catRes.data) {
        setCategories(catRes.data);
      } else {
        setCategories([]);
      }
    };

    fetchChannelsAndCategories();

    // Both handlers below call this same fetch, and the fetch itself issues two
    // queries (channels + categories) in parallel. Without debouncing, a change
    // that touches both tables — e.g. creating a category and moving channels
    // into it — fired the pair twice, i.e. four queries for one user action.
    let structureTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleStructureRefetch = () => {
      clearTimeout(structureTimer);
      structureTimer = setTimeout(fetchChannelsAndCategories, 400);
    };

    const channel = supabase
      .channel(`channels_${activeVault.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'channels', filter: `vault_id=eq.${activeVault.id}` }, () => {
        scheduleStructureRefetch();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'categories', filter: `vault_id=eq.${activeVault.id}` }, () => {
        scheduleStructureRefetch();
      })
      .subscribe();

    return () => {
      clearTimeout(structureTimer);
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, activeVault?.id]);

  // 3. Voice Presence channel
  useEffect(() => {
    if (!isAuthenticated || !activeVault) { setVoiceParticipants({}); return; }

    const channel = supabase.channel(`voice_presence_${activeVault.id}`);
    voicePresenceChannelRef.current = channel;

    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      const participants: Record<string, any[]> = {};
      const newPeerStates: Record<string, PeerVoiceState> = {};
      const latestPresenceByUser = new Map<string, any>();

      Object.keys(state).forEach(key => {
        const presences = state[key] as any[];
        presences.forEach(p => {
          if (!p.userId) return;
          
          // Strict source-of-truth for the local user:
          // Ignore any presence from the server that doesn't match our exact local state.
          // This prevents "ghost" clones in other channels if untrack is delayed or connections overlap.
          if (p.userId === currentUser?.id) {
            if (!isCallActiveRef.current) return;
            if (p.channelId !== callChannelIdRef.current) return;
          }

          const existing = latestPresenceByUser.get(p.userId);
          // If we already have a presence for this user, we need to decide which one to keep.
          // If the new one matches the channel we are currently in, we prefer it (so we see them if they join our channel).
          if (!existing || p.channelId === callChannelIdRef.current) {
            latestPresenceByUser.set(p.userId, p);
          }
        });
      });

      latestPresenceByUser.forEach(p => {
        const chanId = p.channelId || 'vc';
        if (!participants[chanId]) participants[chanId] = [];

        participants[chanId].push({
          id: p.userId,
          name: p.name,
          avatar: p.avatar,
          isMuted: p.isMuted,
          isCameraOn: p.isCameraOn,
          isScreenSharing: p.isScreenSharing,
          isWatchTogetherHost: p.isWatchTogetherHost,
          currentGame: p.currentGame ?? null,
          currentGameSessionId: p.currentGameSessionId ?? null
        });

        newPeerStates[p.userId] = {
          isMuted: p.isMuted,
          isCameraOn: p.isCameraOn,
          isScreenSharing: p.isScreenSharing,
          currentGame: p.currentGame ?? null,
          currentGameSessionId: p.currentGameSessionId ?? null
        };
      });

      // Design fixtures: presence only ever contains browsers that are
      // actually connected, so with one tab open the call screen is a single
      // tile and most of its states are unreachable. Merge the fixture
      // participants into whichever channel the local user is in, so the
      // grid, the active-speaker ring, and the muted/camera-off/screen-share
      // variants are all visible. See `fixtures/callFixtures.ts`.
      if (FIXTURES_ENABLED && isCallActiveRef.current) {
        const chanId = callChannelIdRef.current || 'vc';
        participants[chanId] = [
          ...(participants[chanId] || []),
          ...fixtureParticipantsForChannel(),
        ];
        Object.assign(newPeerStates, fixturePeerVoiceStates());
      }

      setVoiceParticipants(participants);
      setPeerVoiceStates(prev => ({ ...prev, ...newPeerStates }));
    });


    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED' && isCallActive && callChannelId && currentUser) {
        await channel.track({
          userId: currentUser.id,
          name: userProfile.name,
          avatar: userProfile.avatar,
          channelId: callChannelId,
          isMuted,
          isCameraOn: isVideoOn,
          isScreenSharing,
          isWatchTogetherHost,
          currentGame: currentGame ?? null,
          currentGameSessionId: currentGameSessionId ?? null
        }).catch(e => console.error("Presence track failed", e));
      }
    });

    return () => {
      supabase.removeChannel(channel);
      voicePresenceChannelRef.current = null;
    };
  }, [isAuthenticated, activeVault?.id]);

  // 4. Update track when local voice state changes & add tab close cleanup listener
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (voicePresenceChannelRef.current) {
        voicePresenceChannelRef.current.untrack().catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    if (isCallActive && callChannelId && voicePresenceChannelRef.current && currentUser) {
      const updatePresence = async () => {
        if (lastTrackedChannelRef.current && lastTrackedChannelRef.current !== callChannelId) {
          await voicePresenceChannelRef.current?.untrack().catch(() => {});
        }
        lastTrackedChannelRef.current = callChannelId;

        await voicePresenceChannelRef.current?.track({
          userId: currentUser.id,
          name: userProfile.name,
          avatar: userProfile.avatar,
          channelId: callChannelId,
          isMuted,
          isCameraOn: isVideoOn,
          isScreenSharing,
          isWatchTogetherHost,
          currentGame: currentGame ?? null,
          currentGameSessionId: currentGameSessionId ?? null
        }).catch((e: any) => console.error("Presence track update failed", e));
      };
      updatePresence();
    } else if (!isCallActive && voicePresenceChannelRef.current) {
      voicePresenceChannelRef.current.untrack().catch(() => {});
      lastTrackedChannelRef.current = null;
      setVoiceParticipants(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(chanId => {
          updated[chanId] = (updated[chanId] || []).filter(p => p.id !== currentUser?.id);
        });
        return updated;
      });
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isCallActive, callChannelId, userProfile.name, userProfile.avatar, isMuted, isVideoOn, isScreenSharing, isWatchTogetherHost, currentUser?.id, currentGame, currentGameSessionId]);

  // 5. Sync WebRTC peers when voice participants change
  useEffect(() => {
    if (isCallActive && callChannelId && webRTCManagerRef.current) {
       const participants = voiceParticipants[callChannelId] || [];
       const peerIds = participants.map(p => p.id);
       webRTCManagerRef.current.syncPeers(peerIds);
    }
  }, [voiceParticipants, isCallActive, callChannelId]);

  // 6. Messages subscription & fetching
  // The fetchMessages path now:
  //   • Uses an AbortController per effect run (auth-swap race fix).
  //   • Tags messages whose decrypt roundtrip produced no UTF-8 with
  //     `decryptionFailed: true` (when the input looked like a base64
  //     ciphertext), so the bubble renderer shows a 🔒 placeholder
  //     instead of leaking the raw ciphertext into the chat.
  useEffect(() => {
    if (!isAuthenticated || (!activeVault && !activeContactId)) { setMessages([]); setTypingUsers({}); return; }

    const abortCtrl = new AbortController();
    const { signal } = abortCtrl;

    const fetchMessages = async () => {
      if (signal.aborted) return;
      const user = currentUser;
      if (signal.aborted || !user) return;

      let vaultId: string | null = null;
      if (activeVault) {
        vaultId = activeVault.id;
      } else if (activeContactId) {
        const existingDM = dmRooms.find(r => r.members?.includes(activeContactId));
        if (existingDM) {
          vaultId = existingDM.id;
        }
      }

      if (signal.aborted) return;
      if (!vaultId) {
        setMessages([]);
        setMessagesLoading(false);
        return;
      }

      const targetChannelId = activeVault ? (activeChannelId || 'general') : null;
      setMessagesLoading(true);

      let query = supabase
        .from('messages')
        .select('id, sender_id, sender_name, sender_avatar, content, created_at, channel_id, expires_at, reactions')
        .eq('vault_id', vaultId);

      if (activeVault && targetChannelId) {
        query = query.eq('channel_id', targetChannelId);
      } else if (!activeVault) {
        query = query.is('channel_id', null);
      }

      const { data } = await query
        .order('created_at', { ascending: false })
        .limit(100);

      if (signal.aborted) return;
      if (data) {
        const msgs = data.map(m => {
          if (m.expires_at && new Date(m.expires_at).getTime() < Date.now()) return null;

          // Decrypt with failure surfacing. When the ciphertext looks
          // encrypted (long, base64-only) but fails to decrypt, mark
          // `decryptionFailed` so the bubble renders a placeholder
          // instead of leaking the raw base64 to the user.
          const decryptResult = tryDecrypt(m.content, vaultId!);
          const decryptedText = decryptResult.plaintext;
          const decryptionFailed = !decryptResult.ok && decryptResult.looksEncrypted;

          const time = new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

          return {
            id: m.id,
            text: decryptedText,
            time: time,
            type: m.sender_id === user.id ? 'sent' : 'received',
            senderId: m.sender_id,
            senderName: m.sender_name,
            senderAvatar: m.sender_avatar,
            reactions: m.reactions || {},
            expiresAt: m.expires_at ? new Date(m.expires_at).getTime() : undefined,
            decryptionFailed,
          };
        }).filter(Boolean) as Message[];

        if (signal.aborted) return;
        setMessages(msgs.reverse());
      }
      if (signal.aborted) return;
      setMessagesLoading(false);
    };

    fetchMessages();

    let channel: any;
    let isSubscribed = true;

    // This subscription IS server-side filtered (vault_id=eq.…), so it only
    // wakes for the room you're looking at. But each event still triggered a
    // full 100-row refetch, so a burst (several people typing at once, or a
    // paste of multiple lines) fired several overlapping queries. 150ms is
    // short enough to still read as real-time in chat, but collapses a burst
    // into one query.
    let msgRefetchTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleMessagesRefetch = () => {
      clearTimeout(msgRefetchTimer);
      msgRefetchTimer = setTimeout(() => {
        if (!signal.aborted) fetchMessages();
      }, 150);
    };

    const setupSubscription = async () => {
      if (signal.aborted) return;
      const user = currentUser;
      if (!user || !isSubscribed) return;

      let vaultId: string | null = null;
      if (activeVault) {
        vaultId = activeVault.id;
      } else if (activeContactId) {
        const existingDM = dmRooms.find(r => r.members?.includes(activeContactId));
        if (existingDM) {
          vaultId = existingDM.id;
        }
      }

      if (!vaultId) return;

      channel = supabase
        .channel(`messages_${vaultId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'messages', filter: `vault_id=eq.${vaultId}` }, () => {
          scheduleMessagesRefetch();
        })
        .subscribe();
    };

    setupSubscription();

    return () => {
      isSubscribed = false;
      clearTimeout(msgRefetchTimer);
      abortCtrl.abort();
      if (channel) {
        supabase.removeChannel(channel);
      }
    };

  }, [isAuthenticated, activeVault?.id, activeChannelId, activeContactId]);
}
